import { useState } from "react";
import axios from "axios";
import { Image as ImageIcon, Video, MapPin, Smile, Sparkles, X, Send } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { currentUser as guestUser } from "@/lib/mockData";
import PhotoEditor from "@/components/photo/PhotoEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Composer({ onPublished }) {
  const auth = useAuth();
  const me = auth?.user;
  const isAuthed = me && typeof me === "object";
  const display = isAuthed ? me : guestUser;
  const avatarSrc = isAuthed
    ? (me.avatar || `https://api.dicebear.com/7.x/notionists/svg?seed=${encodeURIComponent(me.email)}`)
    : guestUser.avatar;

  const [text, setText] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [publishing, setPublishing] = useState(false);

  const onApply = (result) => {
    if (!result) return;
    if (typeof result === "string") setPhoto({ src: result });
    else setPhoto(result);
    if (result?.signature) toast.success("Filtro «Ovamx Original» aplicado · sello PRO activo");
    else toast.success("Foto editada lista para publicar");
  };

  const canPublish = (text.trim().length > 0 || !!photo) && !publishing;

  const publish = async () => {
    if (!canPublish) return;
    if (!isAuthed) { toast.error("Inicia sesión para publicar"); return; }
    setPublishing(true);
    try {
      const { data } = await axios.post(`${API}/posts`, {
        user_id: me.id,
        user_name: me.name,
        user_avatar: avatarSrc,
        text: text.trim(),
        photo_data_url: photo?.src || null,
        filter_css: photo?.filter || null,
        signature: !!photo?.signature,
        mode: "vinculos",
      });
      toast.success("Publicado en Vínculos · +Sparks ganados");
      setText("");
      setPhoto(null);
      onPublished?.(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo publicar");
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div data-testid="composer" className="border hairline bg-cream-card rounded-2xl p-4 mb-5">
      {!isAuthed && (
        <div data-testid="composer-guest-banner" className="mb-3 p-3 rounded-xl bg-coral/10 border border-coral/30 text-xs text-ink flex items-center gap-2">
          Estás navegando como invitado. <Link to="/login" className="underline font-medium">Inicia sesión</Link> o <Link to="/register" className="underline font-medium">crea una cuenta</Link> para publicar.
        </div>
      )}
      <div className="flex items-start gap-3">
        <img src={avatarSrc} alt="" className="w-10 h-10 rounded-full ring-2 ring-coral/30 bg-cream-deep" />
        <div className="flex-1 min-w-0">
          <textarea
            data-testid="composer-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={isAuthed ? `¿Qué estás pensando, ${display.name?.split(" ")[0] || "Tú"}?` : "Inicia sesión para publicar…"}
            disabled={!isAuthed}
            rows={text.length > 80 || photo ? 3 : 1}
            className="w-full bg-cream border hairline rounded-2xl px-4 py-3 text-sm placeholder:text-ink-mid outline-none focus:bg-cream-card focus:border-coral/60 transition resize-none disabled:opacity-60"
          />

          {photo && (
            <div data-testid="composer-photo-preview" className="mt-3 relative inline-block">
              <img
                src={photo.src}
                alt="preview"
                style={photo.filter ? { filter: photo.filter } : undefined}
                className="max-h-56 rounded-xl border hairline"
              />
              {photo.signature && (
                <span data-testid="composer-signature-badge" className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-coral to-ink text-cream text-[10px] font-mono uppercase tracking-[0.18em]">
                  <Sparkles className="w-2.5 h-2.5" /> Original
                </span>
              )}
              <button onClick={() => setPhoto(null)} data-testid="composer-photo-remove"
                className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-ink text-cream flex items-center justify-center hover:bg-coral transition" aria-label="Quitar foto">
                <X className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => setEditorOpen(true)} data-testid="composer-photo-edit"
                className="absolute bottom-2 right-2 text-[10px] font-mono uppercase tracking-[0.2em] bg-ink/80 text-cream px-2.5 py-1 rounded-full hover:bg-coral transition">
                Editar
              </button>
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              <ComposerAction icon={ImageIcon} label="Foto"   testid="composer-photo" onClick={() => isAuthed && setEditorOpen(true)} disabled={!isAuthed} />
              <ComposerAction icon={Video}     label="Video"  testid="composer-video"  disabled={!isAuthed} />
              <ComposerAction icon={MapPin}    label="Lugar"  testid="composer-place"  disabled={!isAuthed} />
              <ComposerAction icon={Smile}     label="Sentir" testid="composer-feeling" disabled={!isAuthed} />
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="composer-ai-assist"
                className="hidden md:inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs text-ink/70 hover:bg-cream-deep hover:text-ink transition disabled:opacity-50"
                disabled={!isAuthed}>
                <Sparkles className="w-3.5 h-3.5" /> IA: redactar por mí
              </button>
              <button onClick={publish} disabled={!canPublish || !isAuthed}
                data-testid="composer-publish"
                className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-coral text-cream text-sm font-medium hover:bg-coral-hover transition disabled:opacity-40 disabled:cursor-not-allowed">
                <Send className="w-3.5 h-3.5" /> {publishing ? "Publicando…" : "Publicar"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <PhotoEditor open={editorOpen} initialSrc={photo?.src || null} onClose={() => setEditorOpen(false)} onApply={onApply} />
    </div>
  );
}

function ComposerAction({ icon: Icon, label, testid, onClick, disabled }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled}
      className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs text-ink/70 hover:bg-cream-deep hover:text-ink transition disabled:opacity-40 disabled:cursor-not-allowed">
      <Icon className="w-4 h-4" strokeWidth={1.7} />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}
